import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ChatCircle, CheckCircle, Warning, X, Plus, Trash, Copy, ArrowClockwise,
  ShieldCheck, Eye, EyeSlash, DeviceMobile, Info,
} from "@phosphor-icons/react";

/**
 * AdminSmsPhotos — configure the Twilio MMS batch-upload pipeline.
 *
 * Three sections:
 *   1. Connection — SID + Auth Token + phone number, plus a live "Test" button
 *   2. Whitelist — the field-guy phone numbers we accept photos from
 *   3. Activity log — last 50 inbound texts + what happened to each
 */
export default function AdminSmsPhotos() {
  const [cfg, setCfg]         = useState(null);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm]       = useState({ account_sid: "", auth_token: "", phone_number: "" });
  const [whitelist, setWhitelist] = useState([]);
  const [newPhone, setNewPhone]   = useState({ phone_number: "", name: "" });
  const [log, setLog]         = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const [c, w, l] = await Promise.all([
        api.get("/settings/sms-photos/config"),
        api.get("/settings/sms-photos/whitelist"),
        api.get("/settings/sms-photos/log"),
      ]);
      setCfg(c.data);
      setWhitelist(w.data.items || []);
      setLog(l.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't load SMS config"));
    }
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await api.post("/settings/sms-photos/config", form);
      setCfg(r.data);
      setForm({ account_sid: "", auth_token: "", phone_number: "" });
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/sms-photos/test-connection");
      toast.success(`Connected · ${r.data.friendly_name || "Twilio account"} · ${r.data.twilio_account_status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Test failed"));
      await load();
    } finally {
      setTesting(false);
    }
  };

  const addPhone = async () => {
    if (!newPhone.phone_number.trim()) return;
    try {
      await api.post("/settings/sms-photos/whitelist", { ...newPhone, active: true });
      setNewPhone({ phone_number: "", name: "" });
      toast.success("Added");
      const w = await api.get("/settings/sms-photos/whitelist");
      setWhitelist(w.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed"));
    }
  };

  const removePhone = async (id) => {
    if (!window.confirm("Remove this phone from the whitelist?")) return;
    try {
      await api.delete(`/settings/sms-photos/whitelist/${id}`);
      setWhitelist(l => l.filter(w => w.id !== id));
    } catch (e) {
      toast.error("Failed");
    }
  };

  const refreshLog = async () => {
    setRefreshing(true);
    try {
      const l = await api.get("/settings/sms-photos/log");
      setLog(l.data.items || []);
    } finally { setRefreshing(false); }
  };

  const copy = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success("Copied");
    } catch {
      // Clipboard API blocked (permission or non-HTTPS) — fall back to a
      // hidden textarea + document.execCommand so the user isn't stranded.
      try {
        const ta = document.createElement("textarea");
        ta.value = val;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Copied");
      } catch {
        toast.error("Couldn't copy — select the URL and copy manually");
      }
    }
  };

  const connected = cfg?.connected;
  const testFailed = cfg?.last_tested_at && cfg?.last_test_ok === false;
  // Only show the green "connected" banner when creds are saved AND the last
  // live test either passed or hasn't been tried yet — otherwise the red test-
  // failed banner would sit right under a misleading green one.
  const readyBadge = connected && !testFailed;

  return (
    <AdminShell>
      <div className="p-4 sm:p-8 max-w-5xl" data-testid="sms-photos-page">
        <div className="flex items-center gap-2 mb-1 text-xs font-black uppercase tracking-widest text-slate-500">
          <ChatCircle weight="bold" size={12}/> SMS · MMS
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">SMS Photo Uploads</h1>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Field guys text photos to a Twilio number, we auto-match each to a recently-closed WO by GPS + Claude Vision,
          reply with a ✓ or a "which WO?" list. Zero app-opens for your team.
        </p>

        {/* Connection card */}
        <section className="mt-6 rugged-card p-5" data-testid="sms-connection-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck weight="fill" size={20} className={readyBadge ? "text-emerald-500" : testFailed ? "text-red-500" : "text-slate-400"}/>
              <div>
                <div className="font-display font-black text-lg uppercase tracking-wide">Connection</div>
                <div className={`text-[11px] font-black uppercase tracking-widest ${readyBadge ? "text-emerald-700" : testFailed ? "text-red-700" : "text-amber-700"}`}>
                  {readyBadge ? "Connected — ready to receive photos"
                   : testFailed ? "Credentials saved — last test failed, click Test connection"
                   : connected ? "Credentials saved — click Test connection to verify"
                   : "Not connected — paste your Twilio creds"}
                </div>
              </div>
            </div>
            {connected && (
              <button
                onClick={testConnection}
                disabled={testing}
                data-testid="sms-test-connection-btn"
                className="h-9 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-50 font-black uppercase text-[10px] tracking-widest disabled:opacity-60">
                {testing ? "Testing…" : "Test connection"}
              </button>
            )}
          </div>

          {testFailed && (
            <div className="mb-3 p-2 border-2 border-red-400 bg-red-50 text-xs text-red-800">
              Last test failed: <span className="font-mono">{cfg?.last_test_error}</span>
            </div>
          )}

          <div className="grid gap-3">
            <FieldRow
              label="Account SID"
              placeholder="AC…"
              value={form.account_sid || cfg?.account_sid || ""}
              current={cfg?.account_sid_masked}
              onChange={(v) => setForm(f => ({ ...f, account_sid: v }))}
              testId="sms-sid-input"/>
            <FieldRow
              label="Auth Token"
              placeholder="32 hex chars — copy from Twilio's API keys page"
              value={form.auth_token || ""}
              current={cfg?.auth_token_masked}
              secret
              onChange={(v) => setForm(f => ({ ...f, auth_token: v }))}
              testId="sms-token-input"/>
            <FieldRow
              label="Phone Number"
              placeholder="+12677622552"
              value={form.phone_number || cfg?.phone_number || ""}
              current={cfg?.phone_number}
              onChange={(v) => setForm(f => ({ ...f, phone_number: v }))}
              testId="sms-number-input"/>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={saveConfig}
              disabled={saving}
              data-testid="sms-save-config-btn"
              className="h-10 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest disabled:opacity-60">
              {saving ? "Saving…" : "Save credentials"}
            </button>
            {!connected && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                <Info weight="fill" size={12}/> Auth Token is encrypted at rest — never logged in plaintext.
              </div>
            )}
          </div>

          {/* Webhook URL — what to paste into Twilio */}
          {connected && cfg?.webhook_url && (
            <div className="mt-4 p-3 border-2 border-cyan-500 bg-cyan-50">
              <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800 flex items-center gap-1">
                <Info weight="fill" size={10}/> Step 3 · Wire your Twilio number's webhook
              </div>
              <div className="mt-1 text-xs text-slate-700">
                In Twilio Console → your number → <b>Messaging → "A MESSAGE COMES IN"</b>, set:
              </div>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 p-2 bg-white border border-cyan-300 font-mono text-[11px] break-all">
                  {cfg.webhook_url}
                </code>
                <button
                  onClick={() => copy(cfg.webhook_url)}
                  className="h-9 px-2 border-2 border-cyan-500 bg-white hover:bg-cyan-100"
                  data-testid="sms-copy-webhook-btn">
                  <Copy weight="bold" size={14}/>
                </button>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">Method: <b>HTTP POST</b></div>
            </div>
          )}
        </section>

        {/* Whitelist */}
        <section className="mt-6 rugged-card p-5" data-testid="sms-whitelist-card">
          <div className="flex items-center gap-2 mb-3">
            <DeviceMobile weight="fill" size={20} className="text-slate-700"/>
            <div>
              <div className="font-display font-black text-lg uppercase tracking-wide">Field-guy whitelist</div>
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                Only these numbers can text photos in
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 mb-3">
            <input
              value={newPhone.phone_number}
              onChange={e => setNewPhone(p => ({ ...p, phone_number: e.target.value }))}
              placeholder="+12155551234 or 2155551234"
              data-testid="sms-add-phone-input"
              className="h-10 px-3 border-2 border-[#0f172a] font-mono text-sm"/>
            <input
              value={newPhone.name}
              onChange={e => setNewPhone(p => ({ ...p, name: e.target.value }))}
              placeholder="Kevin (Plumber)"
              data-testid="sms-add-phone-name"
              className="h-10 px-3 border-2 border-[#0f172a] text-sm"/>
            <button
              onClick={addPhone}
              data-testid="sms-add-phone-btn"
              className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-1">
              <Plus weight="bold" size={14}/> Add
            </button>
          </div>

          {whitelist.length === 0 ? (
            <div className="p-4 border-2 border-dashed border-slate-300 text-center text-xs text-slate-500">
              No whitelisted phones yet. Add at least one before you go live.
            </div>
          ) : (
            <div className="divide-y-2 divide-slate-100 border-2 border-slate-200">
              {whitelist.map(w => (
                <div key={w.id} className="flex items-center gap-3 px-3 py-2" data-testid={`sms-whitelist-row-${w.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm">{w.phone_number}</div>
                    <div className="text-[11px] text-slate-500">
                      {w.name || "(no name)"} · {w.photos_received} photos received
                      {w.last_seen_at && ` · last seen ${new Date(w.last_seen_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <button
                    onClick={() => removePhone(w.id)}
                    className="h-8 px-2 border-2 border-red-500 text-red-600 font-black uppercase text-[10px] tracking-widest hover:bg-red-50"
                    data-testid={`sms-whitelist-remove-${w.id}`}>
                    <Trash weight="bold" size={12}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Activity log */}
        <section className="mt-6 rugged-card p-5" data-testid="sms-log-card">
          <div className="flex items-center gap-2 mb-3">
            <ChatCircle weight="fill" size={20} className="text-slate-700"/>
            <div className="flex-1">
              <div className="font-display font-black text-lg uppercase tracking-wide">Recent activity</div>
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                Last 50 inbound texts
              </div>
            </div>
            <button
              onClick={refreshLog}
              className={`h-9 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-50 ${refreshing ? "animate-spin" : ""}`}
              data-testid="sms-log-refresh">
              <ArrowClockwise weight="bold" size={14}/>
            </button>
          </div>
          {log.length === 0 ? (
            <div className="p-4 border-2 border-dashed border-slate-300 text-center text-xs text-slate-500">
              No inbound messages yet. Text your Twilio number after you wire the webhook — activity shows here in real-time.
            </div>
          ) : (
            <div className="divide-y-2 divide-slate-100 border-2 border-slate-200 max-h-[500px] overflow-y-auto">
              {log.map(row => <LogRow key={row.id} row={row}/>)}
            </div>
          )}
        </section>

        <TotalCost log={log}/>
      </div>
    </AdminShell>
  );
}

function FieldRow({ label, value, current, placeholder, secret = false, onChange, testId }) {
  const [show, setShow] = useState(!secret);
  return (
    <div>
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
        {label}
        {current && (
          <span className="ml-auto text-slate-400 normal-case tracking-normal font-mono text-[10px]">
            saved: <b>{current}</b>
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type={show ? "text" : "password"}
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          data-testid={testId}
          className="flex-1 h-10 px-3 border-2 border-[#0f172a] font-mono text-sm"/>
        {secret && (
          <button
            type="button"
            onClick={() => setShow(v => !v)}
            data-testid={`${testId}-toggle`}
            className="h-10 px-3 border-2 border-slate-300 text-slate-600 hover:bg-slate-50"
            aria-label={show ? "Hide token" : "Show token"}>
            {show ? <Eye weight="bold" size={14}/> : <EyeSlash weight="bold" size={14}/>}
          </button>
        )}
      </div>
    </div>
  );
}

function LogRow({ row }) {
  const badge = (() => {
    switch (row.outcome) {
      case "attached":              return { color: "bg-emerald-100 text-emerald-800", icon: <CheckCircle weight="fill" size={10}/>, text: "attached" };
      case "needs_review":          return { color: "bg-amber-100 text-amber-800",   icon: <Warning weight="fill" size={10}/>,   text: "review" };
      case "resolved_pending":      return { color: "bg-cyan-100 text-cyan-800",     icon: <CheckCircle weight="fill" size={10}/>, text: "picked" };
      case "rejected_not_whitelisted": return { color: "bg-slate-100 text-slate-700", icon: <X weight="bold" size={10}/>,        text: "dropped" };
      case "rejected_signature":    return { color: "bg-red-100 text-red-800",       icon: <X weight="bold" size={10}/>,        text: "spoofed" };
      default:                      return { color: "bg-slate-100 text-slate-700",   icon: null,                                text: row.outcome };
    }
  })();
  return (
    <div className="px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${badge.color}`}>
          {badge.icon} {badge.text}
        </span>
        <div className="font-mono truncate">{row.from_number}</div>
        {row.from_name && <div className="text-slate-500 truncate">({row.from_name})</div>}
        <div className="text-slate-400 ml-auto whitespace-nowrap">{new Date(row.received_at).toLocaleString()}</div>
      </div>
      <div className="mt-0.5 flex items-center gap-2 flex-wrap text-slate-600">
        {row.num_media > 0 && <span>{row.num_media} 📎</span>}
        {row.body && <span className="italic truncate max-w-[300px]">"{row.body}"</span>}
        {row.matched_wo_address && <span className="text-emerald-700">→ {row.matched_wo_address}</span>}
        {row.reply_sent && (
          <span className="text-slate-500 italic truncate max-w-[400px]">
            ↩ {row.reply_sent.split("\n")[0]}
          </span>
        )}
        {row.cost_estimate_usd > 0 && <span className="text-slate-400 ml-auto">${row.cost_estimate_usd.toFixed(3)}</span>}
      </div>
    </div>
  );
}

function TotalCost({ log }) {
  const total = log.reduce((sum, r) => sum + (r.cost_estimate_usd || 0), 0);
  const count = log.filter(r => r.outcome === "attached" || r.outcome === "resolved_pending").length;
  if (!log.length) return null;
  return (
    <div className="mt-3 text-[11px] text-slate-500 text-right">
      Rough spend in last 50 messages: <b className="font-mono">${total.toFixed(2)}</b> · {count} photos attached
    </div>
  );
}
